import { useNavigate } from 'react-router-dom';
import { getBranding } from '../pages/LoginPage';

interface NoAccessProps {
  /** Optional human-readable module/sub-module the route required. */
  requirementLabel?: string;
  /**
   * Variant:
   * - 'route': user is signed in with access, but not to this destination.
   * - 'config': signed in with a valid role but no accessible modules (or a
   *   permission load error) — a provisioning/configuration problem.
   */
  variant?: 'route' | 'config';
  /** For the 'config' variant. */
  onSignOut?: () => void;
  detail?: string;
}

/**
 * PRD.268 — reusable No-Access screen for route-level denial and for the
 * entry-level "valid role but no accessible permissions" case.
 */
export function NoAccess({
  requirementLabel,
  variant = 'route',
  onSignOut,
  detail,
}: NoAccessProps) {
  const navigate = useNavigate();
  const branding = getBranding();
  const primaryColor = branding.theme?.primaryColor || '#2563eb';

  const isConfig = variant === 'config';
  const title = isConfig ? 'No Modules Available' : 'Access Restricted';
  const message = isConfig
    ? 'Your account is signed in but has no accessible modules configured. Please contact your administrator to finish setting up your role permissions.'
    : "You don't have permission to open this page. If you believe this is an error, contact your administrator.";

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        {branding.orgLogoUrl && (
          <img src={branding.orgLogoUrl} alt={branding.orgName} className="h-14 mx-auto mb-6" />
        )}
        <div
          className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center"
          style={{ backgroundColor: `${primaryColor}20` }}
        >
          <svg
            className="w-8 h-8"
            style={{ color: primaryColor }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
        </div>
        <h1 className="text-xl font-bold text-slate-900 mb-2">{title}</h1>
        <p className="text-slate-600 text-sm mb-6">{message}</p>

        {requirementLabel && (
          <div className="bg-slate-50 rounded-xl p-3 mb-6 text-left text-sm">
            <span className="text-slate-500 font-medium">Requires: </span>
            <span className="text-slate-900 font-mono">{requirementLabel}</span>
          </div>
        )}
        {detail && <p className="text-xs text-slate-400 mb-6 font-mono break-words">{detail}</p>}

        {isConfig ? (
          onSignOut && (
            <button
              onClick={onSignOut}
              className="w-full py-2.5 px-4 text-sm font-semibold text-white rounded-xl shadow-sm hover:shadow-md transition-all"
              style={{ backgroundColor: primaryColor }}
            >
              Sign Out
            </button>
          )
        ) : (
          <button
            onClick={() => navigate('/')}
            className="w-full py-2.5 px-4 text-sm font-semibold text-white rounded-xl shadow-sm hover:shadow-md transition-all"
            style={{ backgroundColor: primaryColor }}
          >
            Go to Home
          </button>
        )}
      </div>
    </div>
  );
}
